import React, { useCallback, useEffect, useState } from "react";
import { Phone, MessageCircle, Footprints, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { isoToDisplay } from "./DateInput";

const PERIODE = [["1w", "1 Minggu"], ["1m", "1 Bulan"], ["3m", "3 Bulan"], ["6m", "6 Bulan"], ["1y", "1 Tahun"]];
const IKON = { "Kunjungan Fisik": Footprints, Telepon: Phone, WhatsApp: MessageCircle };
const WARNA = { "Kunjungan Fisik": "bg-emerald-50 text-emerald-700 border-emerald-200", Telepon: "bg-sky-50 text-sky-700 border-sky-200", WhatsApp: "bg-green-50 text-green-700 border-green-200" };
const today = () => new Date().toISOString().slice(0, 10);
const jamNow = () => new Date().toTimeString().slice(0, 5);

const Stat = ({ id, label, value, sub }) => (
  <Card className="p-4 rounded-2xl" data-testid={`rekap-stat-${id}`}>
    <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
    <div className="text-2xl font-bold text-slate-900 mt-1">{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-0.5 truncate">{sub}</div>}
  </Card>
);

const FormAktivitas = ({ anggota, onSaved }) => {
  const [f, setF] = useState({ tanggal: today(), jam: jamNow(), jemaat_id: "", jenis_kontak: "WhatsApp", catatan: "" });
  const [saving, setSaving] = useState(false);
  const simpan = async () => {
    if (!f.jemaat_id) return toast.error("Pilih anggota yang dihubungi");
    setSaving(true);
    try { await api.post("/pastoral/aktivitas", { ...f, catatan: f.catatan || null }); toast.success("Aktivitas dicatat"); setF({ ...f, jemaat_id: "", catatan: "", jam: jamNow() }); onSaved(); }
    catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <Card className="p-4 rounded-2xl space-y-3" data-testid="aktivitas-form">
      <div className="font-semibold text-slate-900 flex items-center gap-2"><Plus className="h-4 w-4" /> Catat Kontak dengan Anggota</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Input type="date" value={f.tanggal} onChange={(e) => setF({ ...f, tanggal: e.target.value })} className="min-h-[44px]" data-testid="aktivitas-tanggal" />
        <Input type="time" value={f.jam} onChange={(e) => setF({ ...f, jam: e.target.value })} className="min-h-[44px]" data-testid="aktivitas-jam" />
        <select value={f.jemaat_id} onChange={(e) => setF({ ...f, jemaat_id: e.target.value })} className="min-h-[44px] rounded-xl border border-input bg-background px-3 text-sm" data-testid="aktivitas-anggota">
          <option value="">Pilih anggota…</option>{anggota.map((a) => <option key={a.id} value={a.id}>{a.nama_lengkap}</option>)}
        </select>
        <select value={f.jenis_kontak} onChange={(e) => setF({ ...f, jenis_kontak: e.target.value })} className="min-h-[44px] rounded-xl border border-input bg-background px-3 text-sm" data-testid="aktivitas-jenis">
          {Object.keys(IKON).map((k) => <option key={k}>{k}</option>)}
        </select>
      </div>
      <Textarea value={f.catatan} onChange={(e) => setF({ ...f, catatan: e.target.value })} placeholder="Catatan singkat (opsional)" rows={2} className="rounded-xl" data-testid="aktivitas-catatan" />
      <Button onClick={simpan} disabled={saving} className="rounded-xl min-h-[44px]" data-testid="aktivitas-simpan">Simpan Aktivitas</Button>
    </Card>
  );
};

export const RekapAktivitas = () => {
  const { user } = useAuth();
  const isCL = user.role === "CARE_LEADER";
  const [periode, setPeriode] = useState("1m");
  const [clId, setClId] = useState("");
  const [cls, setCls] = useState([]);
  const [anggota, setAnggota] = useState([]);
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/pastoral/aktivitas", { params: { periode, care_leader_id: clId || undefined } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), [periode, clId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (isCL) api.get("/pastoral/anggota-saya").then((r) => setAnggota(r.data.items)).catch(() => {});
    else api.get("/care-leader").then((r) => setCls(r.data.items)).catch(() => {});
  }, [isCL]);
  const hapus = async (id) => { if (!window.confirm("Hapus aktivitas ini?")) return; try { await api.delete(`/pastoral/aktivitas/${id}`); toast.success("Dihapus"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const r = data?.ringkasan;
  return (
    <div className="space-y-4" data-testid="rekap-aktivitas">
      {isCL && <FormAktivitas anggota={anggota} onSaved={load} />}
      <Card className="p-4 rounded-2xl flex flex-wrap gap-2 items-center">
        {!isCL && <select value={clId} onChange={(e) => setClId(e.target.value)} className="min-h-[44px] rounded-xl border border-input bg-background px-3 text-sm" data-testid="rekap-cl-select"><option value="">Semua Care Leader</option>{cls.map((c) => <option key={c.id} value={c.id}>{c.nama}</option>)}</select>}
        {PERIODE.map(([k, l]) => <Button key={k} size="sm" variant={periode === k ? "default" : "outline"} className="rounded-xl min-h-[40px]" onClick={() => setPeriode(k)} data-testid={`rekap-periode-${k}`}>{l}</Button>)}
        {data && <span className="text-xs text-slate-500 ml-auto" data-testid="rekap-periode-label">{isoToDisplay(data.periode.dari)} s/d {isoToDisplay(data.periode.sampai)}</span>}
      </Card>
      {r && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat id="total" label="Total Kontak" value={r.total_kontak} sub={`Kunjungan ${r.per_jenis["Kunjungan Fisik"]} · Telp ${r.per_jenis.Telepon} · WA ${r.per_jenis.WhatsApp}`} />
          <Stat id="dihubungi" label="Anggota Dihubungi" value={r.anggota_dihubungi} sub={`dari ${r.total_anggota} anggota`} />
          <Stat id="belum" label="Belum Dihubungi" value={r.anggota_belum} sub={data.belum_dihubungi.slice(0, 3).map((a) => a.nama_lengkap).join(", ") + (r.anggota_belum > 3 ? ", …" : "")} />
          <Stat id="aktif" label="Care Leader Paling Aktif" value={r.paling_aktif ? r.paling_aktif.nama : "-"} sub={r.paling_aktif ? `${r.paling_aktif.total} kontak` : "Belum ada aktivitas"} />
        </div>
      )}
      <Card className="rounded-2xl overflow-hidden" data-testid="rekap-tabel">
        {data && data.items.length === 0 ? <p className="p-6 text-sm text-slate-500 text-center" data-testid="rekap-kosong">Belum ada aktivitas pada periode ini.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr><th className="text-left p-3">Care Leader</th><th className="text-left p-3">Anggota</th><th className="text-left p-3">Jenis Kontak</th><th className="text-left p-3">Hari</th><th className="text-left p-3">Tanggal</th><th className="text-left p-3">Jam</th><th className="text-left p-3">Catatan</th><th className="p-3" /></tr></thead>
              <tbody>
                {(data?.items || []).map((a) => { const Ik = IKON[a.jenis_kontak] || Phone; return (
                  <tr key={a.id} className="border-t" data-testid={`rekap-baris-${a.id}`}>
                    <td className="p-3 font-medium text-slate-800">{a.care_leader_nama}</td><td className="p-3">{a.jemaat_nama}</td>
                    <td className="p-3"><span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${WARNA[a.jenis_kontak] || ""}`}><Ik className="h-3 w-3" />{a.jenis_kontak}</span></td>
                    <td className="p-3">{a.hari}</td><td className="p-3">{isoToDisplay(a.tanggal)}</td><td className="p-3 font-mono">{a.jam}</td>
                    <td className="p-3 text-slate-600 max-w-[260px] truncate" title={a.catatan || ""}>{a.catatan || "-"}</td>
                    <td className="p-3 text-right">{(a.user_id === user.id || user.role === "ADMIN") && <button onClick={() => hapus(a.id)} className="text-slate-400 hover:text-red-600" aria-label="Hapus" data-testid={`rekap-hapus-${a.id}`}><Trash2 className="h-4 w-4" /></button>}</td>
                  </tr>); })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {data && data.belum_dihubungi.length > 0 && (
        <Card className="p-4 rounded-2xl" data-testid="rekap-belum-list">
          <div className="text-sm font-semibold text-slate-800 mb-2">Anggota belum dihubungi pada periode ini ({data.belum_dihubungi.length})</div>
          <div className="flex flex-wrap gap-2">{data.belum_dihubungi.map((a) => <span key={a.id} className="rounded-full border border-amber-200 bg-amber-50 text-amber-900 px-3 py-1 text-xs">{a.nama_lengkap}</span>)}</div>
        </Card>
      )}
    </div>
  );
};
